import { useEffect, useRef } from 'react';
import { useReducedMotion } from './motion';
import { PASTELS, TONE_HEX } from './tones';

/**
 * Sticker confetti: flat pastel bits with ink outlines, thrown up and falling for 1.4 s on a
 * canvas over its parent (the parent needs `position: relative`). Each change of `trigger`
 * fires one burst (`fireOnMount` adds one on mount). Draws nothing under reduced motion.
 *
 *   <div className="relative"><ConfettiBurst trigger={tierUpCount} /> ...</div>
 */
export interface ConfettiBurstProps {
  /** Any value; a new value fires a burst. null or undefined never fires. */
  trigger: unknown;
  colors?: readonly string[];
  count?: number;
  duration?: number;
  /** Burst once on mount too (the reveal). Default: only on changes. */
  fireOnMount?: boolean;
  className?: string;
}

interface Bit {
  x: number;
  y: number;
  vx: number;
  vy: number;
  w: number;
  h: number;
  rot: number;
  vr: number;
  color: string;
}

const DEFAULT_COLORS = PASTELS.map((t) => TONE_HEX[t]);

export function ConfettiBurst({
  trigger,
  colors = DEFAULT_COLORS,
  count = 60,
  duration = 1400,
  fireOnMount = false,
  className,
}: ConfettiBurstProps) {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLCanvasElement>(null);
  const last = useRef<unknown>(fireOnMount ? Symbol('mount') : trigger);

  useEffect(() => {
    if (reduced || trigger === null || trigger === undefined || trigger === last.current) return;
    const prev = last.current;
    last.current = trigger;
    let finished = false;
    const canvas = ref.current;
    const ctx = canvas?.getContext?.('2d');
    if (!canvas || !ctx) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const { width, height } = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, Math.round(width * dpr));
    canvas.height = Math.max(1, Math.round(height * dpr));
    ctx.scale(dpr, dpr);

    const bits: Bit[] = Array.from({ length: count }, (_, i) => ({
      x: width / 2,
      y: height * 0.6,
      vx: (Math.random() - 0.5) * 9,
      vy: -6 - Math.random() * 8,
      w: 7 + Math.random() * 6,
      h: 4 + Math.random() * 4,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.4,
      color: colors[i % colors.length] ?? '#ffffa1',
    }));

    let raf = 0;
    let t0: number | null = null;
    const frame = (ts: number) => {
      if (t0 === null) t0 = ts;
      const elapsed = ts - t0;
      ctx.clearRect(0, 0, width, height);
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = '#000';
      for (const b of bits) {
        b.vy += 0.32;
        b.vx *= 0.99;
        b.x += b.vx;
        b.y += b.vy;
        b.rot += b.vr;
        ctx.save();
        ctx.translate(b.x, b.y);
        ctx.rotate(b.rot);
        ctx.fillStyle = b.color;
        ctx.fillRect(-b.w / 2, -b.h / 2, b.w, b.h);
        ctx.strokeRect(-b.w / 2, -b.h / 2, b.w, b.h);
        ctx.restore();
      }
      if (elapsed < duration) raf = requestAnimationFrame(frame);
      else {
        finished = true;
        ctx.clearRect(0, 0, width, height);
      }
    };
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      // Cut short (StrictMode remount, a new trigger): let the next run fire again.
      if (!finished) last.current = prev;
      ctx.clearRect(0, 0, width, height);
    };
  }, [trigger, reduced, count, duration, colors]);

  if (reduced) return null;
  return (
    <canvas ref={ref} className={`pointer-events-none absolute inset-0 z-(--z-reveal) size-full ${className ?? ''}`} />
  );
}
